import { useEffect } from 'react'
import { Badge } from '@/components/shadcn/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { RestSchemas } from '@/contexts/QueryProvider'

type AppSettingsResultItem = RestSchemas['Messages.AppSettings.AppSettingsResultItem']

export type OrgMapping = { sourceName: string; destinationName: string }

type OrgMappingSectionProps = {
  items: AppSettingsResultItem[]
  availableOrgs: string[]
  value: OrgMapping[]
  onChange: (mappings: OrgMapping[]) => void
}

const CREATE_NEW_SENTINEL = '__create_new__'

function autoMatch(sourceName: string, availableOrgs: string[]): string {
  return availableOrgs.find((n) => n.toLowerCase() === sourceName.toLowerCase()) ?? CREATE_NEW_SENTINEL
}

function getStatusBadgeVariant(status: string): 'warning' | 'success' {
  return status === 'exists' ? 'success' : 'warning'
}

export function OrgMappingSection({ items, availableOrgs, value, onChange }: OrgMappingSectionProps) {
  // Auto-initialise mappings when items or availableOrgs are first available
  useEffect(() => {
    if (items.length === 0) return
    const initialised = new Set(value.map((m) => m.sourceName))
    const newMappings = items
      .filter((item) => item.elementName && !initialised.has(item.elementName))
      .map((item) => ({
        sourceName: item.elementName!,
        destinationName: autoMatch(item.elementName!, availableOrgs),
      }))
    if (newMappings.length > 0) {
      onChange([...value, ...newMappings])
    }
  }, [items, availableOrgs]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (sourceName: string, destinationName: string) => {
    onChange(value.map((m) => (m.sourceName === sourceName ? { ...m, destinationName } : m)))
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Organisations</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No items in current configuration or uploaded file</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base font-semibold">Organisations</CardTitle>
          <p className="text-xs text-muted-foreground">Map each source organisation to a destination organisation</p>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source organisation</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[240px]">Destination organisation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const sourceName = item.elementName ?? ''
              const status = String(item.status ?? '')
              const mapping = value.find((m) => m.sourceName === sourceName)
              const selectedValue = mapping?.destinationName ?? CREATE_NEW_SENTINEL

              return (
                <TableRow key={sourceName}>
                  <TableCell className="font-medium">{sourceName}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Select value={selectedValue} onValueChange={(val) => handleChange(sourceName, val)}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select organisation..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CREATE_NEW_SENTINEL}>
                          <span className="italic text-muted-foreground">Create new ("{sourceName}")</span>
                        </SelectItem>
                        {availableOrgs.map((orgName) => (
                          <SelectItem key={orgName} value={orgName}>
                            {orgName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
